import clsx from "clsx";
import { List } from "@material-ui/core";

import NavItem from "./NavItem";
import { RenderNavItemsProps, ReduceChildRoutesProps } from "./types";
import { NavigationEnvironmentPicker } from "./EnvironmentPicker";

export function renderNavItems({
  collapsed,
  depth = 0,
  items = [],
  pathname,
  key,
  subheader,
  ...rest
}: RenderNavItemsProps) {
  const filteredItems = items.filter(
    (item) => !item.shouldShowFn || item.shouldShowFn(),
  );
  const isSubItem = depth > 0;

  if (!filteredItems.length) return <></>;

  return (
    <List
      key={key}
      className={clsx(isSubItem ? "!p-0" : collapsed ? "!p-1" : "!p-1.5")}
      role={isSubItem ? "group" : "menu"}
      aria-label={subheader || (isSubItem ? undefined : "Main navigation")}
    >
      {filteredItems.reduce(
        (acc, item) =>
          reduceChildRoutes({ acc, item, pathname, depth, collapsed, ...rest }),
        [] as JSX.Element[],
      )}
    </List>
  );
}

function reduceChildRoutes({
  acc,
  collapsed,
  pathname = "",
  item,
  depth = 0,
}: ReduceChildRoutesProps) {
  if (item.environmentPicker) {
    acc.push(
      <NavigationEnvironmentPicker
        collapsed={collapsed}
        key={`${item.href}${item.title}-picker`}
        className="!m-0 !mb-2"
      />,
    );
    if (!collapsed) {
      if (item.items) {
        acc.push(
          renderNavItems({
            collapsed,
            depth: depth + 1,
            pathname,
            items: item.items,
            key: `${item.href}${item.title}-items`,
          }),
        );
      }
      return acc;
    }
  }

  if (item.items) {
    // Check if any child item matches the current pathname
    const hasMatchingChild = item.items.some((child) => {
      if (child.href === pathname) return true;
      if (child.activeHrefPrefixes) {
        return child.activeHrefPrefixes.some((prefix) =>
          pathname.startsWith(prefix),
        );
      }
      return false;
    });

    // Only open if there's a direct child match, not just a partial parent match
    const open = hasMatchingChild || (item.href && pathname === item.href);

    acc.push(
      <NavItem
        collapsed={collapsed}
        depth={depth}
        icon={item.icon}
        key={item.href + item.title}
        label={item.label}
        open={Boolean(open)}
        title={item.title}
        className={item.className}
        activeHrefPrefixes={item.activeHrefPrefixes}
        exact={item.exact}
        flag={item.flag}
        newSince={item.newSince}
        ttlDays={item.ttlDays}
        featureKey={item.featureKey}
        tracking={item.tracking}
        customActiveCheck={item.customActiveCheck}
      >
        {renderNavItems({
          collapsed,
          depth: depth + 1,
          pathname,
          items: item.items,
        })}
      </NavItem>,
    );
  } else {
    acc.push(
      <NavItem
        collapsed={collapsed}
        depth={depth}
        href={item.href}
        openNewTab={item.openNewTab}
        onClick={item.onClick}
        icon={item.icon}
        key={item.href + item.title}
        label={item.label}
        title={item.title}
        activeHrefPrefixes={item.activeHrefPrefixes}
        exact={item.exact}
        flag={item.flag}
        newSince={item.newSince}
        ttlDays={item.ttlDays}
        featureKey={item.featureKey}
        tracking={item.tracking}
        customActiveCheck={item.customActiveCheck}
        statusBadge={item.statusBadge}
        statusPanel={item.statusPanel}
        statusLabel={item.statusLabel}
      />,
    );
  }

  return acc;
}
